import { ABaseModel } from '../abstracts/ABaseModel.js';
import {
  ICartItemOptionValue,
  ICartItemOption,
  ICartItem,
  ICartAddress,
  ICartMessage,
  ICart,
  ICartItemAction,
  ICartItemResult,
} from '../interfaces/Cart.js';
import { ImageHierarchical } from './Image.js';

/**
 * CartItemOptionValue
 */
class CartItemOptionValue extends ABaseModel implements ICartItemOptionValue {
  id: number;
  optionId: number;
  cartItemId: number;
  title: string;
  price: number;
  total: number;
  selected: boolean;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * CartItemOption
 */
class CartItemOption extends ABaseModel implements ICartItemOption {
  id: number;
  cartItemId: number;
  type: string;
  group: string;
  inputType: string;
  isFrame: boolean;
  isInsurance: boolean;
  required: boolean;
  selected: boolean;
  singleValue: boolean;
  title: string;
  values: Array<CartItemOptionValue>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * CartItem
 */
class CartItem extends ABaseModel implements ICartItem {
  id: number;
  cartId: number;
  key: string;
  freeShipping: boolean;
  noDiscount: boolean;
  qty: number;
  price: number;
  priceNoOptions: number;
  total: number;
  totalNoOptions: number;
  currency: string;
  rentalPeriod: string;
  rentalFrequency: number;
  framed: boolean;
  signed: boolean;
  readyToHang: boolean;
  editionSize: number;
  offerId: number;
  offerAmount: number;
  width: number;
  height: number;
  depth: number;
  units: string;
  incursCustomsDuties: boolean;
  hasItemOptions: boolean;
  hasFrameOptions: boolean;
  hasInsuranceOptions: boolean;
  hasSelectedItemOptions: boolean;
  hasSelectedFrameOptions: boolean;
  hasSelectedInsuranceOptions: boolean;
  shipsBy: string;
  shipsIn: number;
  shipsFromCode: string;
  options: Array<CartItemOption>;
  productId: number;
  productType: string;
  productStatus: number;
  productPublished: string;
  productIsEdition: boolean;
  productIsOriginal: boolean;
  artId: number;
  artStatus: number;
  artTitle: string;
  artSlug: string;
  artYear: number;
  artDescription: string;
  artMaterials: string;
  artMedium: string;
  artSubMedium: string;
  artStyle: string;
  artSubject: string;
  artSubSubject: string;
  artOrientation: string;
  artImages: Array<ImageHierarchical>;
  artistId: number;
  artistAlias: string;
  artistName: string;
  skuId: number;
  skuType: string;
  skuSku: string;
  skuStock: number;
  skuCanBuyInInstalments: boolean;
  sellerId: number;
  sellerIntegrationId: number;
  sellerName: string;
  sellerType: number;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * CartAddress
 */
class CartAddress extends ABaseModel implements ICartAddress {
  id: number;
  cartId: number;
  addressType: string;
  customerId: number;
  email: string;
  telephone: string;
  firstname: string;
  lastname: string;
  company: string;
  streetAddress1: string;
  streetAddress2: string;
  city: string;
  county: string;
  countyCode: string;
  postcode: string;
  countryCode: string;
  customerNotes: string;
  freeShipping: boolean;
  shippingMethod: string;
  shippingDescription: string;
  sameAsBilling: boolean;
  useForShipping: boolean;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * CartMessage
 */
class CartMessage extends ABaseModel implements ICartMessage {
  type: string;
  message: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * Cart
 */
class Cart extends ABaseModel implements ICart {
  id: number;
  isActive: boolean;
  itemsCount: number;
  itemsQty: number;
  storeCode: string;
  currency: string;
  customerId: number;
  customerEmail: string;
  customerFirstname: string;
  customerLastname: string;
  customerNote: string;
  customerCredit: number;
  couponCode: string;
  extShippingInfo: string;
  useCredit: boolean;
  rental: boolean;
  cartItems: Array<CartItem>;
  billingAddress: CartAddress;
  shippingAddress: CartAddress;
  subtotalAmount: number;
  shippingAmount: number;
  discountAmount: number;
  taxAmount: number;
  totalAmount: number;
  creditsAmount: number;
  paymentMethod: string;
  paymentType: string;
  paymentCardType: string;
  paymentCardLastDigits: string;
  paymentCardOwner: string;
  paymentCardEndMonth: number;
  paymentCardEndYear: number;
  paymentCardStartMonth: number;
  paymentCardStartYear: number;
  paymentCardIssue: number;
  savedPaymentId: string;
  savedPaymentCardType: string;
  savedPaymentCardName: string;
  savedPaymentCardLast4: string;
  savedPaymentCardEndMonth: number;
  savedPaymentCardEndYear: number;
  messages: Array<CartMessage>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * CartItemAction
 */
class CartItemAction extends ABaseModel implements ICartItemAction {
  action: string;
  itemId: number;
  artId: number;
  productId: number;
  skuId: number;
  skuNumber: string;
  previousQty: number;
  currentQty: number;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * CartItemResult
 */
class CartItemResult extends ABaseModel implements ICartItemResult {
  actions: Array<CartItemAction>;
  cart: Cart;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

export {
  CartItemOptionValue,
  CartItemOption,
  CartItem,
  CartAddress,
  CartMessage,
  Cart,
  CartItemAction,
  CartItemResult,
};
